import { useState } from 'react'
import useEffectAsync from '../../../../hooks/useEffectAsync'
import { LoadingIndicator } from '../../../../components/LoadingIndicator'
import format from 'date-fns/format'
import nlLocale from 'date-fns/locale/nl'
import { dateTimeToDate } from '../../../../utils/dates'
import Typography from '@material-ui/core/Typography'
import { client } from '../../../../client'
import { useTranslation } from 'react-i18next'

const EndLetterTemplate = ({ programId }) => {
  const { t } = useTranslation('endLetterTemplate')
  const [state, setState] = useState()

  useEffectAsync(async () => {
    const program = await client.getTreatmentProgramById(programId)

    const {
      firstName: psychologistFirstName,
      infix: psychologistInfix,
      lastName: psychologistLastName
    } = program.primaryPsychologist.user
    const {
      infix: patientInfix,
      lastName: patientLastName,
      birthDate: patientBirthDate,
      gender: { salutationShort: salutation },
      street,
      residence,
      houseNumber,
      houseNumberAddition
    } = program.patient

    // Try to resolve activities (2022+); if unavailable just leave fields blank
    let firstDiagnosticDate = ''
    let behandelingCount = ''
    try {
      // Fetch 2022+ activities (source of truth for consult types)
      const activities2022 = await client.getActivities2022ByTreatmentProgram(programId)
      if (Array.isArray(activities2022) && activities2022.length) {
        // Count only consult type id = 2 (Behandeling)
        const behandelingen = activities2022.filter(
          (a) => a?.consultationType?.id === 2
        )
        if (behandelingen.length) {
          behandelingCount = String(behandelingen.length)
        }

        // First diagnostiek date (try id=1; otherwise by name)
        const diagnostiekDates = activities2022
          .filter(
            (a) =>
              a?.consultationType?.id === 1 ||
              (a?.consultationType?.name || '').toLowerCase().includes('diagn')
          )
          .map((a) => a.date)
          .filter(Boolean)
          .sort((a, b) => new Date(a) - new Date(b))
        if (diagnostiekDates.length) {
          firstDiagnosticDate = dateTimeToDate(diagnostiekDates[0])
        }
      } else {
        // Legacy fallback: derive diagnostiek date from legacy activities list if present
        const legacy = Array.isArray(program?.activities) ? program.activities : []
        const diagnostiekDatesLegacy = legacy
          .filter((a) => (a?.activity?.name || '').toLowerCase().includes('diagn'))
          .map((a) => a.date)
          .filter(Boolean)
          .sort((a, b) => new Date(a) - new Date(b))
        if (diagnostiekDatesLegacy.length) {
          firstDiagnosticDate = dateTimeToDate(diagnostiekDatesLegacy[0])
        }
        // We intentionally do NOT try to count behandelingen in legacy list to avoid misclassification
      }
    } catch {
      // Ignore activity fetch errors; fields will stay empty
    }

    setState({
      date: format(new Date(), 'PPP', {
        locale: nlLocale
      }),
      firstDiagnosticDate,
      behandelingCount,
      psychologist: {
        name: [
          psychologistFirstName,
          psychologistInfix,
          psychologistLastName
        ].join(' '),
        phoneNumber: program?.primaryPsychologist?.phoneNumber,
        email:
          program?.practice?.name === 'Mulder & Van Schie'
            ? program?.primaryPsychologist?.user?.secondaryEmail
            : program?.primaryPsychologist?.user?.email
      },
      practiceName: program?.practice?.name,
      patient: {
        name: [salutation.toLowerCase(), patientInfix, patientLastName].join(
          ' '
        ),
        birthDate: dateTimeToDate(patientBirthDate),
        address: `${street} ${houseNumber} ${houseNumberAddition || ''}`,
        residence
      }
    })
  }, [])

  if (!state) {
    return <LoadingIndicator />
  }

  return (
    <div>
      <Typography paragraph variant="body2" align="right">
        {state.date}
      </Typography>

      {/* Betreft */}
      <Typography paragraph variant="body2">
        Betreft: terugkoppeling behandeltraject psychologenpraktijk {state.practiceName}
      </Typography>

      {/* Aanhef + Intro */}
      <Typography paragraph variant="body2">Geachte collega,</Typography>
      <Typography paragraph variant="body2">
        Hierbij brengen wij u op de hoogte van de behandeling die uw patiënt, {state.patient.name}, geboren {state.patient.birthDate}, wonend op {state.patient.address}, te {state.patient.residence}, bij ons volgde.
      </Typography>

      {/* Aanmeldreden */}
      <Typography variant="body2" style={{ fontWeight: 600, marginTop: 8 }}>Aanmeldreden:</Typography>
      <Typography paragraph variant="body2">
        Het intakegesprek in onze praktijk vond plaats op {state.firstDiagnosticDate || '[datum]'}.
        Er was sprake van [KLACHTEN]. Dit probleem bestond sinds ………., ten gevolge van ………… en verergerde in de afgelopen periode. Cliënt rapporteerde (klachten), ……… met als gevolg een belemmering in zijn/haar dagelijks functioneren.
      </Typography>

      {/* Behandelaanbod */}
      <Typography variant="body2" style={{ fontWeight: 600, marginTop: 8 }}>Behandelaanbod:</Typography>
      <Typography paragraph variant="body2">
        Cliënt werd {state.behandelingCount || '[aantal]'} gesprekken aangeboden in onze praktijk, in de vorm van cognitieve gedragstherapie [OF ANDERS, bijv. ACT], gericht op [DOEL].
      </Typography>

      {/* Beloop en afronding */}
      <Typography variant="body2" style={{ fontWeight: 600, marginTop: 8 }}>Beloop behandeling en afronding</Typography>
      <Typography paragraph variant="body2">
        Tijdens de behandeling kreeg cliënt inzicht in [….] door middel van registratieopdrachten. Disfunctionele gedachten met betrekking tot [….] en de invloed van gedragsfactoren op [KLACHT / VERBETERING] werden inzichtelijk en met behulp van cognitief gedragstherapeutische technieken, waaronder gedachtenformulieren en gedragsexperimenten, veranderd. In de laatste sessie is aandacht besteed aan terugvalpreventie. Een aandachtspunt voor de toekomst is/blijft [….].
      </Typography>
      <Typography paragraph variant="body2">
        Cliënt rapporteert bij afronding [VERBETERINGEN EN B+VALKUILEN]. De behandeling binnen onze praktijk is inmiddels (NAAR TEVREDENHEID) afgerond.
      </Typography>

      {/* Afsluiting */}
      <Typography paragraph variant="body2">
        Hopende u hiermee voldoende te hebben geïnformeerd, altijd bereid tot nader overleg.
      </Typography>

      <Typography paragraph variant="body2">Met vriendelijke groet,</Typography>
      <Typography variant="body2">{state.psychologist.name}</Typography>
      <Typography variant="body2">
        Gezondheidszorgpsycholoog bij psychologenpraktijk {state.practiceName}
      </Typography>
      <Typography variant="body2">{state.psychologist.phoneNumber}</Typography>
      <Typography variant="body2">{state.psychologist.email}</Typography>
    </div>
  )
}

export default EndLetterTemplate


